/**
 * Vimeo player for course units and replays.
 */
import React, { useCallback, useEffect, useState } from "react";
import Vimeo from "@u-wave/react-vimeo";
import { useParams } from "react-router-dom";

import { useAsterController } from "context";
import { actionVimeoInspectEdit } from "context/action";

/**
 * Embeds a Vimeo player and persists seek/spent progress.
 *
 * @param {{ vimeoId: string, moduleTitle?: string, vimeoTitle?: string }} props
 */
const VimeoComponent = (props) => {
  const { vimeoId, moduleTitle, vimeoTitle } = props;

  const { courseId } = useParams();

  const [controller, dispatch] = useAsterController();
  const { currVimeo, user } = controller;

  const [isPlaying, setIsPlaying] = useState(false);
  const [forcePause, setForcePause] = useState(false);
  const [seek, setSeek] = useState(null);
  window.seek = seek;
  window.isPlaying = isPlaying;

  /**
   * Flushes remaining watch time when the tab is closing.
   *
   * @returns {void}
   */
  const onBeforeUnload = useCallback(() => {
    if (window.isPlaying) {
      let diff = parseFloat(window.prevTime[1]) - parseFloat(window.seek);
      if (isNaN(diff)) {
        diff = parseFloat(window.prevTime[1]);
      }

      const value = {
        course_id: courseId,
        vimeo_id: vimeoId,
        module_title: moduleTitle,
        vimeo_title: vimeoTitle,
        seek: window.prevTime[1],
        duration: window.duration,
        spent: diff,
        hash: user.hash,
      };
      actionVimeoInspectEdit(dispatch, value);
    }
  }, [courseId, dispatch, moduleTitle, vimeoId, vimeoTitle, user.hash]);

  useEffect(() => {
    if (currVimeo.vimeo_id === vimeoId) {
      setSeek(parseFloat(currVimeo.seek));
    }
  }, [currVimeo, currVimeo.vimeo_id, vimeoId]);

  // Add tab event listener for focus/blur
  useEffect(() => {
    if (window.prevTime === undefined || !Array.isArray(window.prevTime)) {
      window.prevTime = [0.0, 0.0];
    }

    window.addEventListener("focus", onBrowserFocus);
    window.addEventListener("blur", onBrowserBlur);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("focus", onBrowserFocus);
      window.removeEventListener("blur", onBrowserBlur);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [onBeforeUnload]);

  const onBrowserFocus = () => {};

  const onBrowserBlur = () => {
    // if (document.activeElement.tagName !== 'IFRAME') {
    //   setForcePause(true);
    // } else {
    //   setTimeout(() => {
    //     document.activeElement.blur();
    //   }, 100);
    // }
  };

  /**
   * Marks the player as playing and records the current seek.
   *
   * @param {{ seconds?: number }} e Vimeo play event.
   * @returns {void}
   */
  const onPlay = (e) => {
    setIsPlaying(true);
    setForcePause(false);
    if (e) {
      setSeek(e.seconds);
    }
  };

  /**
   * Persists elapsed watch time when the learner pauses.
   *
   * @param {{ seconds: number, duration: number }} e Vimeo pause event.
   * @returns {void}
   */
  const onPause = (e) => {
    const { seconds, duration } = e;
    const diff = seconds - parseFloat(seek);

    const value = {
      course_id: courseId,
      vimeo_id: vimeoId,
      module_title: moduleTitle,
      vimeo_title: vimeoTitle,
      seek: seconds,
      duration: duration,
      spent: diff,
      hash: user.hash,
    };

    actionVimeoInspectEdit(dispatch, value);
    setSeek(seconds);
    setIsPlaying(false);
  };

  /**
   * Persists watch time when the learner scrubs while playing.
   *
   * @param {{ seconds: number, duration: number }} e Vimeo seek event.
   * @returns {void}
   */
  const onSeeked = (e) => {
    const { seconds, duration } = e;
    if (isPlaying) {
      const diff = parseFloat(window.prevTime[0]) - parseFloat(seek);

      const value = {
        course_id: courseId,
        vimeo_id: vimeoId,
        module_title: moduleTitle,
        vimeo_title: vimeoTitle,
        seek: seconds,
        duration: duration,
        spent: diff,
        hash: user.hash,
      };
      actionVimeoInspectEdit(dispatch, value);
    }
    setSeek(seconds);
  };

  /**
   * Tracks the last two playback timestamps for spent-time diffs.
   *
   * @param {{ seconds: number, duration: number }} e Vimeo timeupdate event.
   * @returns {void}
   */
  const onTimeUpdate = (e) => {
    window.prevTime.push(e["seconds"]);
    window.prevTime.shift();
    window.duration = e["duration"];
  };

  return (
    <>{seek !== null && currVimeo.vimeo_id === vimeoId && <Vimeo video={vimeoId} className="as-8-5 w-full" autopause={true} controls={true} responsive={true} color="#f76060" start={seek} paused={forcePause} onPlay={onPlay} onPause={onPause} onSeeked={onSeeked} onTimeUpdate={onTimeUpdate} />}</>
  );
};

export default VimeoComponent;
